import {
  Box,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@mui/material";
import {
  AggregateScoreOrderField,
  AggregateScoreType,
  useGetCveAdvisoriesQuery,
} from "~/operations";
import { memo, useState } from "react";
import { PaginationRange } from "~/components/pagination";
import { FirewatchList } from "~/components/FirewatchList";
import { EmptySection } from "~/components/vulnerabilities";
import { Space } from "~/lib/types";
import { OrderDirection } from "~/components/ui-library";
import { ObservableQuery } from "@apollo/client";
import { useParams } from "react-router-dom";
import { SpaceOrWorkspaceScope } from "~/hooks/useScope";
import { useSort } from "~/pages/inventory/hooks/useSort";

interface SpaceVulnerabilitiesAdvisoriesTableProps {
  space: Space;
  scope: SpaceOrWorkspaceScope;
  scopeMrn: string;
  vulnId?: string;
  assetId?: string;
}

export const SpaceVulnerabilitiesAdvisoriesTable = memo(
  ({
    space,
    scope,
    vulnId,
    assetId,
    scopeMrn,
  }: SpaceVulnerabilitiesAdvisoriesTableProps) => {
    const { id = "" } = useParams();
    const sort = useSort({
      defaultSort: {
        field: AggregateScoreOrderField.RiskValue,
        direction: OrderDirection.Desc,
      },
    });
    const [advisoryPageItems, setAdvisoryPageItems] = useState<PaginationRange>(
      {
        from: 0,
        to: 3,
      },
    );

    const { data, fetchMore, loading } = useGetCveAdvisoriesQuery({
      variables: {
        scopeMrn,
        id: vulnId || id,
        advisoriesFirst: 50,
      },
    });

    const advisoriesConnection =
      data?.cve?.advisoryAggregateScores?.__typename ===
      "AggregateScoresConnection"
        ? data?.cve.advisoryAggregateScores
        : undefined;

    const fetchMoreMitigatedAssets: ObservableQuery["fetchMore"] = (params) => {
      const { variables } = params;
      return fetchMore({
        variables: {
          advisoriesFirst: variables?.first,
          advisoriesAfter: variables?.after,
        },
      });
    };

    // sort advisoriesConnection edges by riskValue
    const sortedAdvisories = advisoriesConnection?.edges
      ? [...advisoriesConnection.edges].sort((a, b) => {
          return (b.node?.riskValue ?? 0) - (a.node?.riskValue ?? 0);
        })
      : undefined;

    const advisories = sortedAdvisories?.slice(
      advisoryPageItems.from,
      advisoryPageItems.to,
    );

    if (loading) {
      return <AdvisoriesTableSkeleton />;
    }

    return (
      <>
        {(advisoriesConnection?.edges?.length || 0) > 0 ? (
          <FirewatchList
            space={space}
            scope={scope}
            pageType={AggregateScoreType.Advisory}
            data={advisories}
            loading={loading}
            sort={sort}
            paginationProps={{
              fetchMore: fetchMoreMitigatedAssets,
              pageInfo: advisoriesConnection?.pageInfo,
              setPageItems: setAdvisoryPageItems,
              totalCount: advisoriesConnection?.totalCount || 0,
              customPageSizes: [3, 10, 25, 50],
            }}
            queryParams={{
              vulnId: vulnId || "",
              assetId: assetId || "",
            }}
            isCVERemediation
          />
        ) : (
          <EmptySection
            id="software-advisories-empty"
            text="There are currently no related advisories."
          />
        )}
      </>
    );
  },
);

const AdvisoriesTableSkeleton = () => {
  return (
    <Paper>
      <Table>
        <TableBody>
          {Array.from({ length: 3 }).map((_, index) => (
            <TableRow key={index}>
              <TableCell width="50px"></TableCell>
              <TableCell width="200px">
                <Skeleton
                  variant="rectangular"
                  height={30}
                  width="40%"
                  animation="wave"
                />
              </TableCell>
              <TableCell width="55%">
                <Skeleton variant="text" width="30%" animation="wave" />
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Skeleton variant="rectangular" height={10} width="50%" />
                  <Skeleton variant="rectangular" height={10} width="30%" />
                </Box>
              </TableCell>

              <TableCell width="200px">
                <Skeleton
                  variant="rectangular"
                  height={30}
                  width="50%"
                  animation="wave"
                />
              </TableCell>
              <TableCell width="200px">
                <Skeleton variant="text" width="50%" animation="wave" />
                <Skeleton variant="text" width="80%" animation="wave" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
};
